import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Form,
  InputGroup,
  Badge,
  Pagination,
  Dropdown,
  Modal,
  Spinner,
  Alert,
} from "react-bootstrap";
import {
  FaSearch,
  FaUserPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaFilter,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaDownload,
  FaEnvelope,
  FaPhone,
  FaBuilding,
  FaCheckCircle,
  FaTimesCircle,
  FaBan,
  FaUserCheck,
  FaUserClock,
  FaUserTie,
  FaUserSlash,
  FaFileExport,
  FaSync,
} from "react-icons/fa";
import { MdBusiness, MdAccessTime, MdEmail } from "react-icons/md";
import { BsBuilding, BsThreeDotsVertical } from "react-icons/bs";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Baseurl } from "../../baseurl";
import { useSelector } from "react-redux";

const ClientList = () => {
  const navigate = useNavigate();
  const { loginData } = useSelector((store) => store.auth);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "desc",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [clientsPerPage] = useState(10);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterAgency, setFilterAgency] = useState("all");
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    available: 0,
    agency: 0,
    independent: 0,
  });

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    filterAndSortClients();
  }, [clients, searchTerm, sortConfig, filterStatus, filterAgency]);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${Baseurl}/api/client/agency/${loginData._id}`,
      );
      console.log(response.data, "response.data");
      setClients(response.data?.clients);
      //   calculateStats(response.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortClients = () => {
    let filtered = [...clients];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (client) =>
          client.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          client.phone?.includes(searchTerm) ||
          client.organisation?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    // Apply status filter
    if (filterStatus !== "all") {
      filtered = filtered.filter((client) =>
        filterStatus === "available"
          ? client.availabilityStatus
          : filterStatus === "unavailable"
            ? !client.availabilityStatus
            : true,
      );
    }

    // Apply agency filter
    if (filterAgency !== "all") {
      filtered = filtered.filter((client) =>
        filterAgency === "agency"
          ? client.workingForAgency
          : filterAgency === "independent"
            ? !client.workingForAgency
            : true,
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === "createdAt") {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });

    setFilteredClients(filtered);
  };

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction:
        sortConfig.key === key && sortConfig.direction === "asc"
          ? "desc"
          : "asc",
    });
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await axios.delete(`${Baseurl}/api/client/deleteclient/${selectedClient._id}`);
      setClients(clients.filter((c) => c._id !== selectedClient._id));
      setShowDeleteModal(false);
    } catch (error) {
      console.error("Error deleting client:", error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="text-muted" />;
    return sortConfig.direction === "asc" ? <FaSortUp /> : <FaSortDown />;
  };

  const getStatusBadge = (status) => {
    if (status) {
      return (
        <Badge bg="success" className="d-flex align-items-center gap-1">
          <FaUserCheck size={12} /> Available
        </Badge>
      );
    }
    return (
      <Badge bg="secondary" className="d-flex align-items-center gap-1">
        <FaUserClock size={12} /> Unavailable
      </Badge>
    );
  };

  const getAgencyBadge = (workingForAgency) => {
    console.log(workingForAgency, "workingForAgency");
    if (workingForAgency) {
      return (
        <Badge bg="info" className="d-flex align-items-center gap-1">
          <FaUserTie size={12} /> Agency
        </Badge>
      );
    }
    return (
      <Badge bg="warning" className="d-flex align-items-center gap-1">
        <FaUserSlash size={12} /> Independent
      </Badge>
    );
  };

  // Pagination
  const indexOfLastClient = currentPage * clientsPerPage;
  const indexOfFirstClient = indexOfLastClient - clientsPerPage;
  const currentClients = filteredClients.slice(
    indexOfFirstClient,
    indexOfLastClient,
  );
  const totalPages = Math.ceil(filteredClients.length / clientsPerPage);

  return (
    <Container fluid className="py-4">
      {/* Header Section */}
      <Row className="mb-4">
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h3 className="mb-1">Clients Management</h3>
                  <p className="text-muted mb-0">
                    Manage and monitor all client accounts
                  </p>
                </div>
                <Button
                  variant="primary"
                  onClick={() => navigate("/clients/add")}
                  className="d-flex align-items-center gap-2"
                >
                  <FaUserPlus />
                  Add New Client
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters and Search */}
      <Row className="mb-4">
        <Col md={8}>
          <InputGroup>
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              placeholder="Search by name, email, phone, or organisation..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <Button
                variant="outline-secondary"
                onClick={() => setSearchTerm("")}
              >
                Clear
              </Button>
            )}
          </InputGroup>
        </Col>
        <Col md={4}>
            <Button variant="outline-secondary me-2" onClick={fetchClients}>
              <FaSync />
            </Button>
        </Col>
      </Row>

      {/* Main Table Card */}
      <Row>
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-3 text-muted">Loading clients...</p>
                </div>
              ) : (
                <>
                  <Table responsive hover className="mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th
                          onClick={() => handleSort("name")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex align-items-center gap-2">
                            Client Name {getSortIcon("name")}
                          </div>
                        </th>
                        <th>Contact</th>
                        <th
                          onClick={() => handleSort("organisation")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex align-items-center gap-2">
                            Organisation {getSortIcon("organisation")}
                          </div>
                        </th>
                        <th>Agency Details</th>
                        <th
                          onClick={() => handleSort("createdAt")}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex align-items-center gap-2">
                            Joined {getSortIcon("createdAt")}
                          </div>
                        </th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentClients.length > 0 ? (
                        currentClients.map((client) => (
                          <tr key={client._id}>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="bg-light rounded-circle p-2 me-3">
                                  <FaUserTie className="text-primary" style={{color:'#2c3e50'}} />
                                </div>
                                <div>
                                  <strong style={{color:'#2c3e50'}}>{client.name || "N/A"}</strong>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="d-flex flex-column">
                                <small className="d-flex align-items-center gap-1">
                                  <FaEnvelope size={12} />{" "}
                                  {client.email || "N/A"}
                                </small>
                                <small className="d-flex align-items-center gap-1">
                                  <FaPhone size={12} /> {client.phone || "N/A"}
                                </small>
                              </div>
                            </td>
                            <td>
                              {client.organisation ? (
                                <div className="d-flex align-items-center gap-2">
                                  <FaBuilding className="text-muted" />
                                  {client.organisation}
                                </div>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              {client?.agency ? (
                                <div className="d-flex flex-column">
                                  <small className="fw-bold">
                                    {client?.agency?.name || "N/A"}
                                  </small>
                                  <small className="text-muted">
                                    {client?.agency?.phn || ""}
                                  </small>
                                  <small className="text-muted">
                                    {client?.agency?.email || ""}
                                  </small>
                                </div>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              {new Date(client.createdAt).toLocaleDateString()}
                            </td>
                            <td>
                              <Dropdown>
                                <Dropdown.Toggle
                                  variant="light"
                                  size="sm"
                                  className="border-0"
                                >
                                  <BsThreeDotsVertical />
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                  <Dropdown.Item
                                    onClick={() =>
                                      navigate(`/admin/clients/${client._id}`)
                                    }
                                  >
                                    <FaEye className="me-2" /> View Details
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    onClick={() =>
                                      navigate(
                                        `/clients/edit/${client._id}`,
                                      )
                                    }
                                  >
                                    <FaEdit className="me-2" /> Edit
                                  </Dropdown.Item>
                                  <Dropdown.Divider />
                                  <Dropdown.Item
                                    className="text-danger"
                                    onClick={() => {
                                      setSelectedClient(client);
                                      setShowDeleteModal(true);
                                    }}
                                  >
                                    <FaTrash className="me-2" /> Delete
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="8" className="text-center py-5">
                            <FaUserTie size={48} className="text-muted mb-3" />
                            <h5>No clients found</h5>
                            <p className="text-muted">
                              {searchTerm ||
                              filterStatus !== "all" ||
                              filterAgency !== "all"
                                ? "Try adjusting your filters"
                                : "Get started by adding your first client"}
                            </p>
                            {!searchTerm &&
                              filterStatus === "all" &&
                              filterAgency === "all" && (
                                <Button
                                  variant="primary"
                                  onClick={() => navigate("/admin/clients/add")}
                                >
                                  <FaUserPlus className="me-2" />
                                  Add Client
                                </Button>
                              )}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>

                  {/* Pagination */}
                  {filteredClients.length > 0 && (
                    <div className="d-flex justify-content-between align-items-center p-3 border-top">
                      <div className="text-muted">
                        Showing {indexOfFirstClient + 1} to{" "}
                        {Math.min(indexOfLastClient, filteredClients.length)} of{" "}
                        {filteredClients.length} clients
                      </div>
                      <Pagination className="mb-0">
                        <Pagination.Prev
                          onClick={() =>
                            setCurrentPage((prev) => Math.max(prev - 1, 1))
                          }
                          disabled={currentPage === 1}
                        />
                        {[...Array(totalPages)].map((_, i) => (
                          <Pagination.Item
                            key={i + 1}
                            active={i + 1 === currentPage}
                            onClick={() => setCurrentPage(i + 1)}
                          >
                            {i + 1}
                          </Pagination.Item>
                        ))}
                        <Pagination.Next
                          onClick={() =>
                            setCurrentPage((prev) =>
                              Math.min(prev + 1, totalPages),
                            )
                          }
                          disabled={currentPage === totalPages}
                        />
                      </Pagination>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filter Modal */}
      <Modal show={showFilterModal} onHide={() => setShowFilterModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaFilter className="me-2" />
            Filter Clients
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="available">Available</option>
                <option value="unavailable">Unavailable</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Client Type</Form.Label>
              <Form.Select
                value={filterAgency}
                onChange={(e) => setFilterAgency(e.target.value)}
              >
                <option value="all">All Types</option>
                <option value="agency">Agency Clients</option>
                <option value="independent">Independent Clients</option>
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setFilterStatus("all");
              setFilterAgency("all");
            }}
          >
            Clear Filters
          </Button>
          <Button variant="primary" onClick={() => setShowFilterModal(false)}>
            Apply Filters
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaTrash className="me-2 text-danger" />
            Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedClient && (
            <p>
              Are you sure you want to delete{" "}
              <strong>{selectedClient.name}</strong>? This action cannot be
              undone.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={deleteLoading}
          >
            {deleteLoading ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Deleting...
              </>
            ) : (
              "Delete Client"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ClientList;
